import { type HTMLAttributes, useMemo } from 'react';
import { flexRender, type Header } from '@tanstack/react-table';
import { clsx } from 'clsx';
import { match } from 'ts-pattern';
import { HorizontalStack } from '../../../alignment/horizontal-stack';
import type { TableColumnDef } from '../../util/column';
import { getSafeColumnId } from '../../util/general';
import { ColumnSort } from './column-sort/column-sort';
import styles from './th.module.scss';

interface THProps<T extends object, TData = unknown> extends HTMLAttributes<HTMLTableCellElement> {
  header: Header<T, TData>;
}

export function TH<T extends object, TData = unknown>({ header, style: providedStyle, ...rest }: THProps<T, TData>) {
  // 'use no memo' directive is required for now because react-table v8 is not compatible with React 19's compiler just yet
  'use no memo';

  const headerId = header.column.id || (typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : '');

  const pin = header?.column.getIsPinned();
  const canSort = header.column.getCanSort();
  const sort = header.column.getIsSorted();
  const resizeHandler = header.getResizeHandler();

  const style = useMemo(() => {
    const id = getSafeColumnId(header.column.id || '');

    return {
      flex: `var(--ixf-table-column-${id}-size)`,
      maxWidth: `var(--ixf-table-column-${id}-max-size)`,
      minWidth: `var(--ixf-table-column-${id}-min-size)`,
      ...providedStyle,
    };
  }, [providedStyle, header.column.id]);

  return (
    <th
      className={clsx(
        styles.th,
        match(pin)
          .with('left', () => styles.isPinnedLeft)
          .with('right', () => styles.isPinnedRight)
          .otherwise(() => undefined),
      )}
      {...rest}
      aria-sort={canSort ? (sort ? (sort === 'desc' ? 'descending' : 'ascending') : 'none') : undefined}
      style={style}
    >
      <HorizontalStack align="space-between" blockAlign="center" className={styles.content} gap={2} wrap="nowrap">
        <div className={clsx(styles.columnName, styles[`align-${(header.column.columnDef as TableColumnDef<T>).align || 'left'}`])}>
          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
        </div>

        {canSort ? (
          <ColumnSort
            direction={sort || undefined}
            name={typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : headerId}
            onSort={header.column.getToggleSortingHandler()}
          />
        ) : null}
      </HorizontalStack>

      {header.column.getCanResize() && (
        <button
          aria-label={`Resize ${typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : headerId} column`}
          className={clsx(styles.resizer, header.column.getIsResizing() && styles.isResizing)}
          onMouseDown={resizeHandler}
          onTouchStart={resizeHandler}
          type="button"
        />
      )}
    </th>
  );
}
